import { brandLinka } from '@ads/tokens';
import type { ProductAvailabilityData } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatNumber } from '@linka/utils';
import { Label, Pie, PieChart } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
  IndicatorCard,
} from '../components';

interface ProductsAvailabilityProps {
  data: ProductAvailabilityData;
}

export const ProductsAvailability = ({ data }: ProductsAvailabilityProps) => {
  const t = useTranslation();
  const totalCapacity = data.totalAvailable != null ? data.totalSold + data.totalAvailable : null;
  const chartData = [
    { name: 'sold', value: data.totalSold, fill: 'var(--color-sold)' },
    {
      name: t('remaining', { count: 1 }),
      value: Math.max(data.totalAvailable ?? 0, 0),
      fill: 'var(--color-available)',
    },
  ];
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.products-sold-vs-available')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_PRODUCTS_AVAILABILITY' />
      </ChartCardHeader>
      <ChartCardContent className='relative'>
        <ChartContainer
          config={{
            sold: { color: brandLinka.baseColor.brand['400'], label: t('sold', { count: 2 }) },
            available: { color: brandLinka.contextualColor.interactive.primary, label: t('remaining', { count: 2 }) },
          }}
          className='min-h-[200px] h-[200px] w-full'
        >
          <PieChart>
            <Pie
              data={chartData}
              startAngle={180}
              endAngle={0}
              innerRadius={80}
              outerRadius={140}
              dataKey='value'
              strokeWidth={0}
              paddingAngle={2}
              cx='50%'
              cy='80%'
            >
              <Label
                content={({ viewBox }) => {
                  if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) return null;
                  return (
                    <text textAnchor='middle'>
                      <tspan x={viewBox.cx} y={viewBox.cy + 40} className='fill-ctx-content-title ts-heading-lg'>
                        {formatNumber(data.totalSold)}
                      </tspan>
                      <tspan x={viewBox.cx} y={viewBox.cy + 58} className='fill-ctx-content-base ts-paragraph-xxxs'>
                        {t('dashboard.products-label')}
                      </tspan>
                    </text>
                  );
                }}
              />
            </Pie>
            <ChartTooltip content={<ChartTooltipContent />} />
          </PieChart>
        </ChartContainer>
        <ChartCardFooter className='flex justify-center gap-nano flex-wrap *:w-full sm:*:w-[30%]'>
          <IndicatorCard label={t('available-quantity')} value={totalCapacity ?? t('unlimited')} />
          <IndicatorCard label={t('sold-so-far')} value={data.totalSold} />
          <IndicatorCard label={t('remaining-quantity')} value={data.totalAvailable ?? t('unlimited')} />
          <IndicatorCard
            label={t('percentage-sold')}
            value={data.soldPercentage ?? t('not-applicable')}
            type='percent'
          />
          <IndicatorCard
            label={t('percentage-remaining')}
            value={data.remainingPercentage ?? t('not-applicable')}
            type='percent'
          />
        </ChartCardFooter>
      </ChartCardContent>
    </ChartCard>
  );
};
